import React from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import * as S from "./styles";
import { theme } from "../../utils/globalStyles/colors";
import {
  AnnouncementLinkType,
  getCategory,
  IAnnouncement,
  resolveAnnouncement,
} from "../../utils/announcements";

interface INewsModal {
  announcement: IAnnouncement | null;
  onClose: () => void;
}

// Texto, cor e ícone do botão conforme o tipo de link do anúncio
const ACTIONS: Record<
  AnnouncementLinkType,
  { label: string; color: string; Icon: SvgIconComponent }
> = {
  whatsapp: {
    label: "Falar no WhatsApp",
    color: "#25d366",
    Icon: WhatsAppIcon,
  },
  menu: {
    label: "Ir para Cardápio",
    color: theme.colors.base,
    Icon: RestaurantMenuIcon,
  },
  site: {
    label: "Visitar site",
    color: theme.colors.secondary,
    Icon: OpenInNewIcon,
  },
};

/* Banner do detalhe: mostra na hora a versão do card (já em cache) e troca
   pela versão maior assim que ela carrega. A `key` pelo id do anúncio monta
   um banner novo a cada anúncio, então nunca aparece a imagem do anterior. */
const SheetBanner: React.FC<{
  announcement: IAnnouncement;
}> = ({ announcement }) => {
  const [detailLoaded, setDetailLoaded] = React.useState(false);
  const cardSrc = resolveAnnouncement(announcement, "card").imageSrc;
  const detailSrc = resolveAnnouncement(announcement, "detail").imageSrc;
  if (!detailSrc) return null;

  return (
    <S.BannerFrame>
      {cardSrc && cardSrc !== detailSrc && (
        <S.BannerImage src={cardSrc} alt="" aria-hidden $visible />
      )}
      <S.BannerImage
        src={detailSrc}
        alt={announcement.title}
        $visible={detailLoaded || cardSrc === detailSrc}
        onLoad={() => setDetailLoaded(true)}
      />
    </S.BannerFrame>
  );
};

const NewsModal: React.FC<INewsModal> = ({ announcement, onClose }) => {
  // Conteúdo exibido: o anúncio aberto agora ou, durante a animação de
  // fechar, o último que estava aberto. Calculado no próprio render (antes era
  // atualizado num efeito, depois de desenhar, e o anúncio anterior aparecia
  // por um instante ao abrir outro).
  const lastRef = React.useRef<IAnnouncement | null>(null);
  if (announcement) lastRef.current = announcement;
  const current = announcement ?? lastRef.current;

  const resolved = current ? resolveAnnouncement(current, "detail") : null;
  const category = getCategory(current?.category);
  const action = ACTIONS[resolved?.linkType ?? "whatsapp"];

  return (
    <S.Sheet
      anchor="bottom"
      open={!!announcement}
      onOpen={() => undefined}
      onClose={onClose}
      disableSwipeToOpen
    >
      <S.Handle />
      {current && resolved && (
        <>
          <SheetBanner key={current.id} announcement={current} />

          <S.ContentWrapper>
            <S.CategoryTag>
              <category.Icon style={{ fontSize: 14 }} />
              {category.label}
            </S.CategoryTag>

            <S.Message>{current.title}</S.Message>

            <S.Message2>{current.description}</S.Message2>

            {resolved.link && (
              <S.ActionButton
                href={resolved.link}
                target="_blank"
                rel="noopener noreferrer"
                $color={action.color}
              >
                <action.Icon />
                {action.label}
              </S.ActionButton>
            )}

            <S.Confirm onClick={onClose} variant="text">
              Fechar
            </S.Confirm>
          </S.ContentWrapper>
        </>
      )}
    </S.Sheet>
  );
};

export default NewsModal;
